// fpjs.js
// Manage FingerprintJs Pro initialization and mixpanel tracking

import { getItem, setItem } from '../resource/persist.js';
import { setFpJsData } from '../resource/mixpanel.js';
import { fingerprint as fingerprintDebug } from '../resource/debug.js';

const log = fingerprintDebug.extend('log:fpjs');

let fpProPromise;

/**
 * Hydrate Mixpanel fingerprint properties from persisted Pro id without requesting
 * a new FingerprintJS Pro result.
 * @returns {Promise<void>}
 * @example
 * await hydrateFpJsData();
 */
export const hydrateFpJsData = async () => {
  try {
    const visitorId = await getItem('_fpId');
    if (!visitorId) return;

    setFpJsData({
      visitorId,
    });
  } catch (error) {
    log('hydrateFpJsData error', { error });
  }
};

/**
 *
 * @param {Object} options
 * @param {String} options.id
 *
 */
export const fpProGetLinkedIdForUserId = async ({ id }) => {
  if (!fpProPromise) return;
  log('fpProGetLinkedIdForUserId start', { id });
  const fpPro = await fpProPromise;
  fpPro.get({ linkedId: `user-${id}` });
  log('fpProGetLinkedIdForUserId done', { id });
};

/**
 *
 * @param {Object} options
 * @param {String} options.fpJsAPIKey
 * @param {String} options.fpJsEndpoint
 * @param {String} options.fpJsRegion
 * @param {String} options.clientId
 * @param {String} options.userId
 */
export const initFpJsPro = async ({
  fpJsAPIKey,
  fpJsEndpoint,
  fpJsRegion,
  clientId,
  userId,
}) => {
  const persistedVisitorId = await getItem('_fpId');
  let visitorId = persistedVisitorId;
  let ip;

  if (fpJsAPIKey && !persistedVisitorId) {
    log('initFpJsPro start', { fpJsAPIKey, fpJsEndpoint, fpJsRegion });
    const FpPro = await import('@fingerprintjs/fingerprintjs-pro');

    const options = {
      apiKey: fpJsAPIKey,
      region: fpJsRegion,
    };

    if (fpJsEndpoint) {
      options.endpoint = fpJsEndpoint;
    }

    fpProPromise = FpPro.load(options);

    const fpPro = await fpProPromise;
    const result = await fpPro.get({
      extendedResult: true,
      linkedId: userId ? `user-${userId}` : `client-${clientId}`,
    });
    visitorId = result?.visitorId;
    ip = result?.ip;

    log('initFpJsPro done', { visitorId, ip });
  }

  setFpJsData({
    visitorId,
    ip,
  });

  if (persistedVisitorId !== visitorId) return setItem('_fpId', visitorId);
};

export const initFpJsOSS = async () => {
  const persistedVisitorId = await getItem('_fpIdOpenSource');
  let visitorId = persistedVisitorId;
  if (!persistedVisitorId) {
    log('initFpJsOSS start');
    const FpOSS = await import('@fingerprintjs/fingerprintjs');
    const fposs = await FpOSS.load();
    const result = await fposs.get();
    visitorId = result?.visitorId;
    log('initFpJsOSS done', { visitorId });
  }
  setFpJsData({
    visitorIdOpenSource: visitorId,
  });

  if (persistedVisitorId !== visitorId)
    return setItem('_fpIdOpenSource', visitorId);
};

export const getFpIdOSS = async () => getItem('_fpIdOpenSource');

export const getFpIdPro = async () => getItem('_fpId');

/**
 * Get a fresh requestId from FingerprintJS Pro.
 * Unlike initFpJsPro (which caches visitorId), this always calls fpPro.get()
 * to produce a new requestId for the backend to query real-time risk data.
 * The same fpPro.get() result also carries the visitorId, so it is returned
 * too — the caller can cache it to save a later initFpJsPro API call.
 *
 * @param {Object} options
 * @param {String} options.fpJsAPIKey
 * @param {String} options.fpJsEndpoint
 * @param {String} options.fpJsRegion
 * @param {String} options.clientId
 * @param {String} options.userId
 * @return {Promise<{requestId: string|undefined, visitorId: string|undefined}>}
 * @example
 * const { requestId, visitorId } = await getFpProRequestId({ fpJsAPIKey });
 */
export const getFpProRequestId = async ({
  fpJsAPIKey,
  fpJsEndpoint,
  fpJsRegion,
  clientId,
  userId,
}) => {
  if (!fpJsAPIKey) return {};

  // Step 1: ensure FP Pro agent is loaded (load is free, not billed)
  if (!fpProPromise) {
    log('getFpProRequestId: loading FpPro agent');
    const FpPro = await import('@fingerprintjs/fingerprintjs-pro');
    const options = { apiKey: fpJsAPIKey, region: fpJsRegion };
    if (fpJsEndpoint) {
      options.endpoint = fpJsEndpoint;
    }
    fpProPromise = FpPro.load(options);
  }

  // Step 2: always call fpPro.get() for a fresh requestId (billed per call)
  const fpPro = await fpProPromise;
  const result = await fpPro.get({
    extendedResult: true,
    linkedId: userId ? `user-${userId}` : `client-${clientId}`,
  });

  log('getFpProRequestId done', { requestId: result?.requestId });
  return { requestId: result?.requestId, visitorId: result?.visitorId };
};
